/**
 *  <p>Defines an API for logging product usage as part of the Data Driven User Experience (DDUX) program.
 *  There are three primary logging methods, each targeting a distinct use case. They are:</P>
 *
 *  <ol>
 *      <li>logData - used for general-purpose logging of key-value pairs</li>
 *      <li>logEvent - used to log user interactions with the UI, such as clicks and key presses</li>
 *      <li>logComposition - used to log the composition of user-produced artifacts such as live editor scripts or Simulink models.</li>
 *   </ol>
 *
 *   <p>The UsageLogger can be utilized in either a standalone web application or a web application backed by
 *   a server-side MATLAB.  In the first case logged data is sent directly to a DDUX server, while in
 *   the second it is sent to the backend MATLAB which will relay it to the DDUX server.  Refer to the
 *   "start" method for details on how to configure the UsageLogger for a particular web application.</p>
 *
 * <p><b>IMPORTANT</b></P>
 *
 * <p>Before adding logging for a product or extending existing logging you must submit a "DDUX data collection proposal"
 * via the MathWorks Proposal Center and obtain approval.</p>
 *
 * @module mw-ddux/UsageLogger
 *
 * @copyright 2018-2022 The MathWorks, Inc.
 */
define(['mw-ddux/DirectSender', 'mw-ddux/MatlabSender', 'mw-log/Log'], function (DirectSender, MatlabSender, Log) {

    var _sender;
    var _uiEventDefaults;
    var _telemetryDefaults;
    var _compositionDefaults;
    var _elementIdPrefixes = {};
    var _taps = [];
    var _selectedContext;

    return {
        _started: false,
        allowProductScopeOverride: false,

        /**
         * <p>Initializes usage logging.  Logging calls made before start or after stop are dropped.</p>
         *
         * @param loggerProperties {object} supplies properties that control the behavior of the logger.
         *        These can include default values for various event attributes.
         * @param loggerProperties.product {string} identifies the product in all UI event and
         *        telemetry logs unless overriden
         * @param loggerProperties.scope {string} identifies the scope in all UI event logs
         *        unless overridden.
         * @param loggerProperties.appComponent {string} identifies the appComponent in all
         *        telemetry logs unless overridden.  "scope" and "appComponent" play a similar roll
         *        in UI event logs and telemetry logs, respecively.  If the same value should be used
         *        for both you may specify either rather than both.
         * @param loggerProperties.eventKey {string} identifies the telemetry stream in all
         *        telemetry logs unless overridden
         * @param loggerProperties.artifactType {string} identifies the type of artifact associated with all
         *        artifact composition logs unless overridden.
         * @param loggerProperties.allowProductScopeOverride {boolean} should be set true if
         *        products and scopes other than those specified above can exist in the same window.
         *        In this case the UsageLogger will look for annotations on the DOM that can be used
         *        to override the default product and scope.
         * @param loggerProperties.sendDirect {boolean} true if logged data should be sent directly to the DDUX
         *        server rather than via a MATLAB backend.
         * @param loggerProperties.applicationName {string} the name of the application logging data
         * @param loggerProperties.applicationVersion {string} the version of the application logging data
         * @param loggerProperties.sendToDev when sending directly to the DDUX server, specifies data should go to
         *        the devevelopment (dev) or production (prod) URL. If not specified data goes to prod if the
         *        page hostname is "mathworks.com" (optional)
         * @param loggerProperties.release {string} The mathworks product release such as 'R2020b'.  If specified
         *        the release is used to determine the dev endpoint for direct send. (optional)
         * @param loggerProperties.endPointNumber {number} 1, 2 or 3 indicating which "integ" endpoint to send to.
         *        If specified this overrides the release-based dev endpoint selection (optional)
         *
         * The following deprecated call signature is also supported for backward compatibility.
         *
         * start: function(product, scope, allowProductScopeOverride)
         *
         * @instance
         */
        start: function start(loggerProperties, legacyParameter1, legacyParameter2) {
            if (this._started) {
                return;
            }
            window.UsageLogger = this;
            // Type check first argument to see if deprecated signature is being used
            if (typeof loggerProperties === 'string') {
                _uiEventDefaults = {
                    product: loggerProperties,
                    scope: legacyParameter1
                };
                _telemetryDefaults = {
                    product: loggerProperties,
                    appComponent: legacyParameter1
                };
                _compositionDefaults = {
                    product: loggerProperties,
                    appComponent: legacyParameter1
                };
                this.allowProductScopeOverride = legacyParameter2; // For WidgetLogger
                loggerProperties = _uiEventDefaults; // For MatlabSender
            } else {
                loggerProperties = loggerProperties || {};
                _uiEventDefaults = {
                    product: loggerProperties.product,
                    scope: loggerProperties.scope || loggerProperties.appComponent,
                    allowProductScopeOverride: loggerProperties.allowProductScopeOverride
                };
                _telemetryDefaults = {
                    product: loggerProperties.product,
                    appComponent: loggerProperties.appComponent || loggerProperties.scope,
                    eventKey: loggerProperties.eventKey
                };
                _compositionDefaults = {
                    product: loggerProperties.product,
                    appComponent: loggerProperties.appComponent || loggerProperties.scope,
                    artifactType: loggerProperties.artifactType,
                    artifactId: loggerProperties.artifactId
                };
                this.allowProductScopeOverride = loggerProperties.allowProductScopeOverride;
            }
            this._started = true;
            _sender = loggerProperties.sendDirect ? DirectSender : MatlabSender;
            _sender.start(loggerProperties);
        },

        /**
         * Terminates usage logging.
         *
         * @instance
         */
        stop: function stop() {
            if (this._started) {
                _sender.stop();
                _sender = undefined;
                this._started = false;
            }
        },

        /**
         * Indicates whether the logger has been started and not stopped
         *
         * @returns {boolean} true if start, but not stop has been called.
         *
         * @instance
         */
        isRunning: function isRunning() {
            return this._started;
        },

        /**
         * Logs a UI event
         *
         * @param eventData {object} an object that contains at a minimum eventType and elementId properties but may also contain elementType and additional string-valued properties
         * @param eventData.eventType {string} identifies the type of event.  Can be any of the following:
         *   "cell_edit",
         *   "clicked",
         *   "closed",
         *   "column_description_changed",
         *   "column_dragged",
         *   "column_sorted",
         *   "deselected",
         *   "docked",
         *   "double_clicked",
         *   "dragged",
         *   "key_pressed",
         *   "location_changed",
         *   "maximized",
         *   "menu_item_selected",
         *   "minimized",
         *   "opened",
         *   "restored",
         *   "scrolled",
         *   "selected",
         *   "selection_changed",
         *   "undocked",
         *   "units_changed",
         *   "variable_opened",
         * @param eventData.elementId {string} identifies the UI element the user interacted with
         * @param eventData.elementType {string} identifies the type of UI element the user interactive with.  Can be any of the following:
         *   "app",
         *   "button",
         *   "combo_box",
         *   "document",
         *   "document_group",
         *   "drop_down",
         *   "list",
         *   "list_item",
         *   "menu",
         *   "menu_item",
         *   "slider",
         *   "spinner",
         *   "table",
         *   "text field",
         *   "text area",
         *   "panel",
         *   "toolstrip",
         *   "toolstrip tab"
         * @param unused
         * @param immediate {boolean} if true the event will be logged immediately.  Otherwise logging may be deferred.
         *
         * @instance
         */
        logEvent: function logEvent(eventData, unused, immediate) {
            if (!this._started) {
                var ancestorLogger = this._getStartedAncestor();
                if (ancestorLogger) {
                    ancestorLogger.logEvent(eventData, unused, immediate);
                    return;
                } else if (!window.UsageLogger) {
                    // Log.warn('UsageLogger start must be called before logging data');
                    return;
                }
            }
            Log.assert(eventData.eventType, 'eventType must be defined');
            if (eventData.elementType) {
                if (!eventData.elementId) {
                    Log.warn('Can\'t log ' + eventData.elementType + ' ' + eventData.eventType + ' without elementId');
                    return;
                }
            }
            immediate = immediate || unused;
            eventData.product = _uiEventDefaults.product;
            eventData.scope = _uiEventDefaults.scope;
            var that = this;
            var doLogEvent = function doLogEvent() {
                var attributes;
                try {
                    that._filterElementId(eventData);
                    // Include the selected context if the event is a keypress or a toolstrip event
                    if (_selectedContext && (eventData.eventType === 'keypress' || attributes && attributes.subscope === 'toolstrip')) {
                        eventData.selection = _selectedContext;
                    }
                    that._sendEvent('uiEvent', eventData);
                } catch (error) {
                    Log.warn('Error while logging event: ' + error.message);
                }
            };
            // Defer activity to avoid potential adverse affect on responsiveness unless instructed otherwise
            if (immediate) {
                doLogEvent();
            } else {
                setTimeout(doLogEvent, 0);
            }
        },

        /**
         * Specifies a context that may be helpful in interpreting keyboard or toolstrip UI events.
         * For example the caller could provide the id associated with the selected region of the UI.
         * This id will be included in subsequent UI event logs as the "selected" field when relevant.
         *
         * @param contextid
         *
         * @instance
         */
        setSelectedContext: function setSelectedContext(context) {
            _selectedContext = context;
        },

        setPrefixesOfElementIdsToRedact: function setPrefixesOfElementIdsToRedact(prefixes, product, scope) {
            _elementIdPrefixes[this._productAndScopeKey(product || _uiEventDefaults.product, scope || _uiEventDefaults.scope)] = prefixes;
        },

        /**
         * Logs arbitrary key-value pairs.  This is the most general purpose logging method.
         *
         * @param data {object} abitrary key value pairs to be logged
         * @param identification {object} optional properties that identify the data source, overriding the
         *        defaults specified via start
         * @param identification.product {string} identifies the product with which the data is associated
         * @param identification.appComponent {string} identifies the app component with which the data is associated
         * @param identification.eventKey {string} identifies the data stream
         * @param options {object} optional handling instructions
         * @param options.verifyParametersOnly {boolean} if true, data will not actually be sent
         *
         * @instance
         */
        logData: function logData(data, identification, options) {
            if (!this._started) {
                var ancestorLogger = this._getStartedAncestor();
                if (ancestorLogger) {
                    ancestorLogger.logEvent(eventData, unused, immediate);
                    return;
                } else if (!window.UsageLogger) {
                    // Log.warn('UsageLogger start must be called before logging data');
                    return;
                }
            }
            this._sendEvent('telemetry', this._composeTelemetryEvent(data, identification, options));
        },

        /**
         * Logs artifact composition.
         *
         * @param identification {object} properties that identify the data source and artifact, overriding the
         *        defaults, if any, specified via start
         * @param identification.product {string} identifies the product with which the artifact is associated
         * @param identification.appComponent {string} identifies the app component with which the artifact is associated
         * @param identification.artifactType {string} indicated the type of artifact
         * @param identification.artifactId {string} uniquely identifies the artifact.  This string will be hashed so that any
         *        contained IP or PII will be obscured.
         * @param properties {object} artifact property names and values to be logged.  The values may be string, boolean or number
         * @param componentCounts {object} artifact component types and corresponding non-zero counts
         *
         * @instance
         */
        logComposition: function logComposition(identification, properties, componentCounts) {
            if (!this._started) {
                var ancestorLogger = this._getStartedAncestor();
                if (ancestorLogger) {
                    ancestorLogger.logEvent(eventData, unused, immediate);
                    return;
                } else if (!window.UsageLogger) {
                    // Log.warn('UsageLogger start must be called before logging data');
                    return;
                }
            }
            identification = identification || {};
            // Compose event
            var event = {
                product: identification.product || _compositionDefaults.product,
                appComponent: identification.appComponent || _compositionDefaults.appComponent,
                artifactType: identification.artifactType || _compositionDefaults.artifactType,
                artifactId: identification.artifactId || _compositionDefaults.artifactId,
                properties: properties,
                componentCounts: componentCounts
            };
            Log.assert(event.product, 'product must be defined');
            Log.assert(event.appComponent, 'appComponent must be defined');
            Log.assert(event.artifactType, 'artifactType must be defined');
            Log.assert(event.artifactId, 'artifactId must be defined');
            this._sendEvent('composition', event);
        },

        /**
         * Supports "taping", i.e. listening in on, the ddux log stream for test.
         *
         * @param matchProperties {object} determines which ddux logs are sent to the given tapFunction.
         *        matchProperties can contain any set of properties and values (including empty) that may
         *        appear in the ddux logs.  Logs matching all the specified properties will be passed to the
         *        tapFunction.  If matchProperties is empty then all logs will be passed.  An example
         *        matchProperties value might be {product: "Image Processing Toolbox", scope: "Camera Calibrator"}.
         * @param tapFunction {function (object)} function to which matching logs will be passed.
         * @param blockDefault {boolean} if true then data sent to the tap function will not be sent on down the pipe.
         * @return an object whose remove() function can be called to remove the tap
         *
         * @instance
         */
        addTap: function addTap(matchProperties, tapFunction, blockDefault) {
            var tap = {
                matchProperties: matchProperties,
                tapFunction: tapFunction,
                blockDefault: blockDefault
            };
            _taps.push(tap);
            return { remove: function remove() {
                    var index = _taps.indexOf(tap);
                    if (index >= 0) {
                        _taps.splice(index, 1);
                    }
                } };
        },

        /**
         * Removes a tap that was added via the addTap method.
         *
         * @param matchProperties {object} the matchProperties that were passed to the addTap call
         * @param tapFunction {function} the function that was passed to the addTap call
         *
         * @deprecated call remove() on the object returned by addTap instead.
         *
         * @instance
         */
        removeTap: function removeTap(matchProperties, tapFunction) {
            for (var i = 0; i < _taps.length; i++) {
                var tap = _taps[i];
                if (tap.tapFunction === tapFunction) {
                    var keys1 = Object.keys(matchProperties);
                    var keys2 = Object.keys(tap.matchProperties);
                    var allMatch = keys1.length === keys2.length;
                    for (var j = 0; j < keys1.legnth && allMatch; j++) {
                        var key = keys1[j];
                        allMatch = matchProperties[key] === tap.matchProperties[key];
                    }
                    if (allMatch) {
                        _taps.splice(i, 1);
                        i--;
                    }
                }
            }
        },

        _filterElementId: function _filterElementId(eventData) {
            var product = eventData.product || _uiEventDefaults.product;
            var scope = eventData.scope || _uiEventDefaults.scope;
            var prefixes = _elementIdPrefixes[this._productAndScopeKey(product, scope)];
            var isNumber = function isNumber(s) {
                return !/\D/.test(s);
            };
            if (prefixes) {
                var i, prefix;
                for (i = 0; i < prefixes.length; i++) {
                    prefix = prefixes[i];
                    if (eventData.elementId.startsWith(prefix)) {
                        var remainder = eventData.elementId.slice(prefix.length);
                        eventData.elementId = prefix + ':redacted';
                        if (remainder.length > 0) {
                            var c = remainder.charAt(0);
                            if (c === ':' || c === '.') {
                                eventData.redactedHash = this.hashString(remainder);
                            } else if (isNumber(remainder)) {
                                eventData.redactedHash = remainder;
                            }
                        }
                        break;
                    }
                }
            }
        },

        _productAndScopeKey: function _productAndScopeKey(product, scope) {
            return product + ':' + scope;
        },

        // TODO: replace with something standard like MD5.
        hashString: function hashString(s) {
            var hash = 0;
            for (var i = 0; i < s.length; i++) {
                hash += Math.pow(s.charCodeAt(i) * 31, s.length - i);
                hash &= hash;
            }
            return Math.abs(hash).toString(16);
        },

        _composeTelemetryEvent: function _composeTelemetryEvent(data, identification, options) {
            identification = identification || {};
            var event = {
                product: identification.product || _telemetryDefaults.product,
                appComponent: identification.appComponent || _telemetryDefaults.appComponent,
                eventKey: identification.eventKey || _telemetryDefaults.eventKey,
                data: data
            };
            if (options) {
                event.verifyParametersOnly = options.verifyParametersOnly;
            }
            Log.assert(event.eventKey, 'eventKey must be defined');
            return event;
        },

        _sendEvent: function _sendEvent(eventType, event) {
            // Send to matching taps
            var blocked = false;
            _taps.forEach(function (tap) {
                var matchKeys = Object.keys(tap.matchProperties);
                var allMatch = true;
                for (var k = 0; k < matchKeys.length && allMatch; k++) {
                    var key = matchKeys[k];
                    allMatch = tap.matchProperties[key] === event[key];
                }
                if (allMatch) {
                    tap.tapFunction(event);
                    if (tap.blockDefault) {
                        blocked = true;
                    }
                }
            });
            if (!blocked) {
                _sender.sendEvent(eventType, event);
            }
        },

        _getStartedAncestor: function _getStartedAncestor() {
            var ancestor = window.opener;
            while (ancestor) {
                var ancestorLogger = ancestor.UsageLogger;
                if (ancestorLogger && ancestorLogger._started) {
                    return ancestorLogger;
                }
                ancestor = ancestor.opener;
            }
            return null;
        }
    };
});
//# sourceMappingURL=UsageLogger.js.map
